import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaExclamationCircle, FaUsers, FaCheckCircle } from 'react-icons/fa';
import '../Login/Login.css';
import allSafeLogo from '../../../assets/cloudsafe-logo.png';
import { getApiEndpoint } from '../../../config/api';
import { getToken } from '../../../utils/auth';
import { getDashboardRoute } from '../../../utils/roleUtils';
import { formatDate } from '../../portals/Contractor/workspace/utils/dates';
import { STAFF_ROLES, staffRoleLabel } from '../../portals/Contractor/workspace/utils/staffRoles';

/**
 * /contractor/invitations/:token — an existing CloudSafe user reviews a team
 * invitation and accepts or declines it. Nothing changes on their account
 * unless they accept (backend: POST /api/contractor/staff/invitations/:token/accept).
 */

async function call(path, method = 'GET') {
  const token = getToken();
  const res = await fetch(getApiEndpoint(path), {
    method,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  let body = null;
  try { body = await res.json(); } catch { body = null; }
  if (!res.ok) {
    const err = new Error(body?.message || `Request failed (${res.status})`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

const STATUS_COPY = {
  accepted: 'You have already accepted this invitation.',
  revoked: 'This invitation has been withdrawn or declined, so it can no longer be used.',
  expired: 'This invitation has expired. Ask the company admin to invite you again.',
};

export default function AcceptInvitation() {
  const { token } = useParams();
  const navigate = useNavigate();
  const signedIn = !!getToken();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(signedIn);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(''); // '' | 'accept' | 'decline'
  const [actionError, setActionError] = useState('');
  const [confirmDecline, setConfirmDecline] = useState(false);
  const [declined, setDeclined] = useState(false);
  // 409 PRIVILEGED_ACCOUNT: a CloudSafe staff/admin account can never join — don't offer Accept again.
  const [privileged, setPrivileged] = useState(false);

  useEffect(() => { document.title = 'Team invitation · CloudSafe'; }, []);

  const load = useCallback(async () => {
    if (!signedIn) return;
    setLoading(true);
    setLoadError(null);
    try {
      setInvite(await call(`/api/contractor/staff/invitations/${encodeURIComponent(token)}`));
    } catch (e) {
      setLoadError(e);
    } finally {
      setLoading(false);
    }
  }, [signedIn, token]);

  useEffect(() => { load(); }, [load]);

  const accept = async () => {
    setBusy('accept');
    setActionError('');
    try {
      const res = await call(`/api/contractor/staff/invitations/${encodeURIComponent(token)}/accept`, 'POST');
      // The server re-reads the role on every request; refresh the client copy
      // so the contractor pages open without signing out and back in.
      const role = res?.role || invite?.role || 'field';
      const admin = role === 'owner' || role === 'admin' || !!invite?.isCompanyAdmin;
      localStorage.setItem('userRole', 'contractor');
      localStorage.setItem('accountType', admin ? 'contractor_admin' : 'contractor_staff');
      if (admin) localStorage.setItem('isCompanyAdmin', 'true');
      else localStorage.removeItem('isCompanyAdmin');
      toast.success(`You've joined ${res?.companyName || invite?.companyName || 'the team'}`);
      navigate(getDashboardRoute('contractor'), { replace: true });
    } catch (e) {
      if (e.body?.code === 'PRIVILEGED_ACCOUNT') {
        setPrivileged(true);
        setActionError(e.body.message || 'This is a CloudSafe staff or admin account, so it can’t join a contractor company. Ask the company to invite a separate work email instead.');
        return;
      }
      setActionError(e.status === 503
        ? 'CloudSafe is having trouble — try again shortly'
        : (e.message || 'Could not accept the invitation — try again.'));
      if (e.status === 409 || e.status === 410) load();
    } finally {
      setBusy('');
    }
  };

  const decline = async () => {
    setBusy('decline');
    setActionError('');
    try {
      await call(`/api/contractor/staff/invitations/${encodeURIComponent(token)}/decline`, 'POST');
      setDeclined(true);
      setConfirmDecline(false);
    } catch (e) {
      setActionError(e.message || 'Could not decline the invitation — try again.');
    } finally {
      setBusy('');
    }
  };

  const nextParam = encodeURIComponent(`/contractor/invitations/${token}`);
  const roleInfo = invite ? STAFF_ROLES.find((r) => r.id === invite.role) : null;

  let content;
  if (!signedIn) {
    content = (
      <>
        <h1 className="login-title">Sign in to see your invitation</h1>
        <p className="login-subtitle">Someone has invited you to join their team on CloudSafe. Sign in with the account the invitation was sent to.</p>
        <Link to={`/login?next=${nextParam}`} className="login-submit" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>
          Sign in
        </Link>
      </>
    );
  } else if (loading) {
    content = <p className="login-subtitle" role="status">Loading invitation…</p>;
  } else if (loadError) {
    const notFound = loadError.status === 404;
    content = (
      <>
        <h1 className="login-title">{notFound ? 'Invitation not found' : "Couldn't load this invitation"}</h1>
        <div className="login-error-alert" role="alert">
          <FaExclamationCircle className="login-error-alert-icon" aria-hidden="true" />
          <span>
            {notFound
              ? "This link doesn't match an invitation for the account you're signed in with. Check you're signed in with the email address the invitation was sent to."
              : (loadError.message || 'Something went wrong — try again.')}
          </span>
        </div>
        {!notFound && <button type="button" className="login-submit" onClick={load}>Try again</button>}
        <Link to={getDashboardRoute()} className="login-secondary" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 12, textDecoration: 'none' }}>
          Go to my dashboard
        </Link>
      </>
    );
  } else if (declined) {
    content = (
      <>
        <h1 className="login-title">Invitation declined</h1>
        <p className="login-subtitle">Nothing has changed on your account. {invite?.companyName || 'The company'} can send you a new invitation if needed.</p>
        <Link to={getDashboardRoute()} className="login-submit" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>
          Go to my dashboard
        </Link>
      </>
    );
  } else if (invite) {
    const pending = invite.status === 'pending';
    content = (
      <>
        <FaUsers aria-hidden="true" style={{ fontSize: 36, color: 'var(--cs-blue, #1A80E5)', marginBottom: 12 }} />
        <h1 className="login-title">Join {invite.companyName}</h1>
        <p className="login-subtitle">
          {invite.companyName} has invited you to join their team on CloudSafe.
        </p>
        <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 16px', margin: '0 0 20px', fontSize: 'var(--cs-text-base)' }}>
          <dt style={{ color: 'var(--cs-text-muted)' }}>Role</dt>
          <dd style={{ margin: 0, fontWeight: 600 }}>
            {staffRoleLabel(invite.role, invite.isCompanyAdmin)}
            {roleInfo && <span style={{ display: 'block', fontWeight: 400, color: 'var(--cs-text-secondary)', fontSize: 'var(--cs-text-sm)' }}>{roleInfo.desc}</span>}
          </dd>
          <dt style={{ color: 'var(--cs-text-muted)' }}>Expires</dt>
          <dd style={{ margin: 0 }}>{formatDate(invite.expiresAt)}</dd>
        </dl>

        {!pending && (
          <div className="login-error-alert" role="status">
            {invite.status === 'accepted'
              ? <FaCheckCircle className="login-error-alert-icon" aria-hidden="true" />
              : <FaExclamationCircle className="login-error-alert-icon" aria-hidden="true" />}
            <span>{STATUS_COPY[invite.status] || 'This invitation can no longer be used.'}</span>
          </div>
        )}

        {actionError && (
          <div className="login-error-alert" role="alert">
            <FaExclamationCircle className="login-error-alert-icon" aria-hidden="true" />
            <span>{actionError}</span>
          </div>
        )}

        {pending && !confirmDecline && (
          <>
            {!privileged && (
              <p style={{ fontSize: 'var(--cs-text-sm)', color: 'var(--cs-text-secondary)', margin: '0 0 16px' }}>
                If you accept, your account is linked to {invite.companyName}. Their admins will be able to assign you jobs and
                see the qualifications on your profile. You keep your own sign-in.
              </p>
            )}
            {!privileged && (
              <button type="button" className="login-submit" onClick={accept} disabled={!!busy}>
                {busy === 'accept' && <span className="login-spinner" />}
                {busy === 'accept' ? 'Joining…' : `Accept and join ${invite.companyName}`}
              </button>
            )}
            <button type="button" className="login-secondary" onClick={() => setConfirmDecline(true)} disabled={!!busy} style={{ marginTop: 12 }}>
              Decline
            </button>
          </>
        )}

        {pending && confirmDecline && (
          <div role="group" aria-label="Confirm decline">
            <p style={{ fontSize: 'var(--cs-text-sm)', color: 'var(--cs-text-secondary)', margin: '0 0 16px' }}>
              Decline this invitation? The link stops working and {invite.companyName} would need to invite you again.
            </p>
            <button type="button" className="login-submit" onClick={decline} disabled={!!busy} style={{ background: 'var(--cs-red-dark, #b91c1c)' }}>
              {busy === 'decline' ? 'Declining…' : 'Yes, decline'}
            </button>
            <button type="button" className="login-secondary" onClick={() => setConfirmDecline(false)} disabled={!!busy} style={{ marginTop: 12 }}>
              Keep the invitation
            </button>
          </div>
        )}

        {!pending && (
          <Link to={getDashboardRoute()} className="login-submit" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>
            Go to my dashboard
          </Link>
        )}
      </>
    );
  }

  return (
    <div className="login-page">
      <header className="login-header">
        <img src={allSafeLogo} alt="CloudSafe" className="login-header-logo" />
      </header>
      <main className="login-body">
        <div className="login-card">{content}</div>
      </main>
    </div>
  );
}
